import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../app/TenantContext";
import { Icon } from "../shell/Icon";
import { AngebotTab } from "./detail/AngebotTab";
import { KlaerpunkteTab } from "./detail/KlaerpunkteTab";
import { MatchTab } from "./detail/MatchTab";
import { MengenTab } from "./detail/MengenTab";
import { PreiseTab } from "./detail/PreiseTab";
import { RunDialog } from "./detail/RunDialog";
import { UebersichtTab } from "./detail/UebersichtTab";

/**
 * Detail-Page im Redesign-Look.
 *
 * Shell: detail-hero (back-button + customer + chips + actions) + segmented
 * tabs. Tabs sind: Uebersicht, [Mengen wenn Pfad B], Match, Preise, Angebot,
 * Klaerpunkte. Mengen-Tab ist im Redesign-Bundle nicht vorgesehen, aber im
 * BDL-Plan (~/.claude/plans/den-part-w-rde-ich-ticklish-hamster.md) als
 * eigenes Feature angekuendigt — hier strukturell vorbereitet.
 */

const STATUS_PILL: Record<string, { label: string; tone: "" | "ok" | "warn" | "bad" | "acc" }> = {
  eingegangen: { label: "EINGEGANGEN", tone: "" },
  in_pruefung: { label: "IN PRÜFUNG", tone: "acc" },
  angebot_erstellt: { label: "ANGEBOT BEREIT", tone: "acc" },
  versendet: { label: "VERSENDET", tone: "ok" },
  gewonnen: { label: "GEWONNEN", tone: "ok" },
  verloren: { label: "VERLOREN", tone: "bad" },
  verworfen: { label: "VERWORFEN", tone: "" },
};

type Tab = "uebersicht" | "mengen" | "match" | "preise" | "angebot" | "klaerpunkte";

/**
 * Cross-Tab-Aktion: aus dem Klaerpunkte-Tab heraus den Match-Tab aufrufen
 * und dort gleich den Drawer oder den Schnellkalk-Dialog fuer eine
 * konkrete Position vorbereiten. Tab + Pending-Action werden hier gehalten,
 * MatchTab verbraucht die Action via Effect und ruft `onConsumed` auf, um den
 * Trigger zu loeschen.
 */
export type PendingMatchAction = {
  positionId: string;
  action: "drawer" | "schnellkalk";
};

export function DetailPage() {
  const { anfrageId = "" } = useParams<{ anfrageId: string }>();
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(
    () => api.getAnfrage(tenantId, anfrageId),
    [tenantId, anfrageId]
  );
  const [tab, setTab] = useState<Tab>("uebersicht");
  const [runOpen, setRunOpen] = useState(false);
  const [pendingMatchAction, setPendingMatchAction] =
    useState<PendingMatchAction | null>(null);

  const klaerCount = useApi(
    () => api.getKlaerpunkte(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  const klaerOffen = useMemo(() => {
    if (klaerCount.state.status !== "ok" || !klaerCount.state.data) return 0;
    return klaerCount.state.data.punkte.filter((p) => !p.resolved).length;
  }, [klaerCount.state]);

  if (state.status === "loading") return <DetailSkeleton />;
  if (state.status === "error") return <DetailError error={state.error} onRetry={refetch} />;

  const { rohdaten, phasenVorhanden } = state.data;
  const pfad = inferPfad(phasenVorhanden, rohdaten);
  const status = STATUS_PILL[rohdaten.status] ?? { label: rohdaten.status.toUpperCase(), tone: "" };

  const tabs: Array<[Tab, string, number?]> = [
    ["uebersicht", "Übersicht"],
    ...(pfad === "B"
      ? ([["mengen", "Mengen"]] as Array<[Tab, string, number?]>)
      : ([] as Array<[Tab, string, number?]>)),
    ["match", "Match"],
    ["preise", "Preise"],
    ["angebot", "Angebot"],
    ["klaerpunkte", "Klärpunkte", klaerOffen],
  ];

  return (
    <div className="screen detail fade-up">
      <div className="detail-hero">
        <div className="detail-hero-l">
          <Link to="/anfragen" className="btn ghost">
            <Icon name="back" size={15} /> Inbox
          </Link>
          <div className="detail-id">
            <div className="uppercase-mini">/ ANFRAGE / DETAIL</div>
            <h1 className="detail-customer">{rohdaten.kunde.name}</h1>
            <div className="detail-meta mono">
              <span className="bv-chip">{rohdaten.bauvorhaben.bezeichnung}</span>
              <span className="id-chip">{anfrageId}</span>
              <span className={`pill ${status.tone}`}>
                <span className="dot" />
                {status.label}
              </span>
            </div>
          </div>
        </div>
        <div className="detail-hero-r">
          <button type="button" className="btn primary" onClick={() => setRunOpen(true)}>
            <Icon name="play" size={14} /> Pipeline starten
          </button>
          <button type="button" className="btn" title="Neu laden" onClick={refetch}>
            <Icon name="refresh" size={14} />
          </button>
        </div>
      </div>

      <div className="tabs">
        {tabs.map(([id, label, badge], i) => (
          <button
            key={id}
            type="button"
            className={`tab ${tab === id ? "active" : ""}`}
            onClick={() => setTab(id)}
          >
            <span className="tab-num mono">{String(i + 1).padStart(2, "0")}</span>
            <span>{label}</span>
            {typeof badge === "number" && badge > 0 && (
              <span className="tab-badge mono">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "uebersicht" && (
        <UebersichtTab
          tenantId={tenantId}
          anfrageId={anfrageId}
          rohdaten={rohdaten}
          phasenVorhanden={phasenVorhanden}
          onRunRequest={() => setRunOpen(true)}
          onRefetch={refetch}
        />
      )}
      {tab === "mengen" && <MengenTab tenantId={tenantId} anfrageId={anfrageId} />}
      {tab === "match" && (
        <MatchTab
          tenantId={tenantId}
          anfrageId={anfrageId}
          onKlaerpunkteMaybeChanged={klaerCount.refetch}
          pendingAction={pendingMatchAction}
          onActionConsumed={() => setPendingMatchAction(null)}
        />
      )}
      {tab === "preise" && <PreiseTab tenantId={tenantId} anfrageId={anfrageId} />}
      {tab === "angebot" && <AngebotTab tenantId={tenantId} anfrageId={anfrageId} />}
      {tab === "klaerpunkte" && (
        <KlaerpunkteTab
          tenantId={tenantId}
          anfrageId={anfrageId}
          onJumpToMatch={(positionId, action) => {
            setPendingMatchAction({ positionId, action });
            setTab("match");
          }}
          onAnyMatchPatch={klaerCount.refetch}
        />
      )}

      {runOpen && (
        <RunDialog
          tenantId={tenantId}
          anfrageId={anfrageId}
          onClose={() => setRunOpen(false)}
          onSuccess={() => {
            refetch();
            klaerCount.refetch();
          }}
        />
      )}
    </div>
  );
}

/**
 * Pfad-Heuristik fuer das Detail: wenn das anfrage-typ-File schon geschrieben
 * wurde (Phase 1), liest die UebersichtTab das echte Pfad-Feld. Hier reicht
 * eine grobe Vorab-Schaetzung fuer die Tab-Sichtbarkeit:
 *  - hat dateien mit pdfKlassifikation.erkanntAls === "bauplan" → B
 *  - hat lvDateien (.x83, .gaeb, ...) → A
 *  - sonst: unbekannt → A (default, kein Mengen-Tab)
 */
function inferPfad(
  _phasenVorhanden: number[],
  rohdaten: { dateien: Array<{ relPath: string; pdfKlassifikation?: { erkanntAls: string; bestaetigt?: boolean } | null }> }
): "A" | "B" | null {
  const hasBauplan = rohdaten.dateien.some(
    (d) =>
      d.pdfKlassifikation?.erkanntAls === "bauplan" &&
      d.pdfKlassifikation?.bestaetigt
  );
  if (hasBauplan) return "B";
  const hasLv = rohdaten.dateien.some((d) =>
    /\.(x8[1-6]|gaeb|xml)$/i.test(d.relPath)
  );
  if (hasLv) return "A";
  return null;
}

function DetailSkeleton() {
  return (
    <div className="screen detail">
      <div className="detail-hero">
        <div className="detail-hero-l">
          <div className="btn ghost">
            <Icon name="back" size={15} /> Inbox
          </div>
          <div className="detail-id">
            <div className="uppercase-mini">/ ANFRAGE / DETAIL</div>
            <h1 className="detail-customer" style={{ opacity: 0.4 }}>
              Lade …
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div className="screen">
      <div className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
            // ANFRAGE NICHT ERREICHBAR
          </span>
        </div>
        <div style={{ display: "grid", gap: 10, color: "var(--fg-mute)", fontSize: 13 }}>
          <div className="mono" style={{ color: "var(--bad)" }}>
            {error.message}
          </div>
          <div>
            <button type="button" className="btn" onClick={onRetry}>
              <Icon name="refresh" size={14} /> Nochmal versuchen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
